import { Component, Input, OnInit } from "@angular/core";
import {
  DASHBOARD_NAVIGATION,
  QA_QUEUE,
  VOLUNTEER_MANAGEMENT_PAGE,
} from "app/access-control/common-permission-asks";
import { hasAccess } from "app/access-control/permission-verification";
import {
  ActivityLog,
  Bereavement,
  Pages,
  QaQueue,
  VolunteerManagement,
} from "app/access-control/permissions";
import { Events, MenuController } from "ionic-angular";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { PermissionsResponse } from "../../../app/access-control/permissions-response";
import { BasePage } from "../../../pages/base-page";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../providers/app-status/app-status";
import { MobileUtilsProvider } from "../../../providers/mobile-utils/mobile-utils";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { NavigationService } from "../../navigation.service";
import { NavigationDataService } from "../../services/navigation-data.service";

@Component({
  selector: "dashboard-navigation-bar",
  templateUrl: "dashboard-navigation-bar.html",
})
export class DashboardNavigationBarComponent
  extends BasePage
  implements OnInit
{
  @Input() activePage: string;
  isPatientCalendarToggleOn: boolean = false;
  appSyncStatus: ConnectionStatusEnum;
  clinicalMember: any;
  isVolunteerAccess: boolean = false;
  permissionsAccess = {} as PermissionsResponse;
  displayQaQueueButton: boolean = false;
  displayQaControlsButton: boolean = false;
  displayOpenVisitsButton: boolean = false;
  hasAccessToBereavement: boolean = false;
  hasAccessToActivityLog: boolean = false;
  hasCalendarPagePermission: boolean = false;
  referralAndIntakeToggle: boolean = false;
  isNewAlertToggleOn: boolean = false;
  QAQueueToggle = false;
  concurrentVisitsToggle = false;

  subscriptions: Subscription[] = [];

  connectionStatusEnum = ConnectionStatusEnum;

  constructor(
    public navData: NavigationDataService,
    public navSvc: NavigationService,
    public stateData: StateManagementService,
    public appStatus: AppStatus,
    public events: Events,
    public mobileUtilsProvider: MobileUtilsProvider,
    public menuCtrl: MenuController,
    private accessControlService: AccessControlService
  ) {
    super(stateData);
    this.appSyncStatus = this.appStatus.getAppStatus();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.stateData.showReferralAndIntake.subscribe((x) => {
        if (x !== null) {
          this.referralAndIntakeToggle = x;
        }
      })
    );
    this.subscriptions.push(
      this.stateData.showNewAlerts.subscribe((value) => {
        this.isNewAlertToggleOn = value;
      })
    );
    this.subscriptions.push(
      this.stateData.checkUserDisciplines().subscribe(
        (data) => {
          if (data["persons"] && data["persons"].length > 0) {
            this.clinicalMember =
              data["persons"][0]["teamSet"].length > 0 ? true : false;
          } else {
            this.clinicalMember = false;
          }
        },
        (error) => {
          this.clinicalMember = false;
        }
      )
    );
    this.subscriptions.push(
      this.stateData.isPatientCalendarToggleOn.subscribe((x) => {
        if (x) {
          this.isPatientCalendarToggleOn = x;
        }
      })
    );

    this.checkDashboardAccess();

    this.subscriptions.push(
      this.accessControlService
        .getPermissionWhenAuthenticated(QA_QUEUE)
        .subscribe(
          (data) => {
            this.permissionsAccess = data;
            this.checkQueueTogglePermissions();
            this.checkOpenVisitsTogglePermission();
          },
          (err) => {
            console.error(
              "QA permission ask failed; QA nav buttons stay hidden",
              err
            );
          }
        )
    );

    this.subscriptions.push(
      this.stateData.qaQueueToggle.subscribe((toggle) => {
        this.QAQueueToggle = !!toggle;
        this.checkQueueTogglePermissions();
      })
    );

    this.subscriptions.push(
      this.stateData.concurrentVisitsToggle.subscribe((toggle) => {
        // Null (never fetched) and false both resolve to "off" per contract.
        this.concurrentVisitsToggle = !!toggle;
        this.checkOpenVisitsTogglePermission();
      })
    );
  }

  private checkQueueTogglePermissions() {
    if (this.QAQueueToggle) {
      this.displayQaQueueButton = this.accessControlService.hasAccess(
        this.permissionsAccess,
        QaQueue.DashboardView
      );
      this.displayQaControlsButton = this.accessControlService.hasAccess(
        this.permissionsAccess,
        QaQueue.CompanyControlsView
      );
    } else {
      this.displayQaQueueButton = false;
      this.displayQaControlsButton = false;
    }
  }

  // CO-3456: the Open Visits entry rides Dashboard:QA:VIEW in v1. A parallel
  // OpenVisits permission needs the IAM permission catalogue seeded outside
  // this repo, and until that lands it would resolve to access:false and hide
  // the entry from everyone.
  private checkOpenVisitsTogglePermission() {
    this.displayOpenVisitsButton =
      this.concurrentVisitsToggle &&
      this.accessControlService.hasAccess(
        this.permissionsAccess,
        QaQueue.DashboardView
      );
  }

  checkDashboardAccess() {
    const permissions = [...VOLUNTEER_MANAGEMENT_PAGE, ...DASHBOARD_NAVIGATION];
    this.subscriptions.push(
      this.accessControlService
        .getPermissionWhenAuthenticated(permissions)
        .subscribe(
          (permissionsAccess) => {
            let isVolunteerAdmin = permissionsAccess
              ? hasAccess(
                  VolunteerManagement.VMPAdministrator,
                  permissionsAccess.permissions
                )
              : false;
            let isVolunteer = permissionsAccess
              ? hasAccess(
                  VolunteerManagement.VMPVolunteer,
                  permissionsAccess.permissions
                )
              : false;
            this.isVolunteerAccess = isVolunteer || isVolunteerAdmin;
            this.hasAccessToActivityLog = permissionsAccess
              ? hasAccess(
                  ActivityLog.ActivityLogView,
                  permissionsAccess.permissions
                )
              : false;
            if (this.mobileUtilsProvider.checkIfMobile()) {
              let vmRbac = {
                voladmin: isVolunteerAdmin,
                volunteer: isVolunteer,
              };
              this.accessControlService.setVolunteerAccessData(vmRbac);
            }

            this.hasAccessToBereavement = permissionsAccess
              ? hasAccess(
                  Bereavement.BereavementView,
                  permissionsAccess.permissions
                )
              : false;
            this.hasCalendarPagePermission = permissionsAccess
              ? hasAccess(Pages.CalendarPage, permissionsAccess.permissions)
              : false;
          },
          (err) => {
            console.error(
              "Dashboard permission ask failed; gated nav buttons stay hidden",
              err
            );
          }
        )
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((val) => val.unsubscribe());
  }

  redirectPage(id: string) {
    this.navSvc.clearPatientSessionContext();
    if (id === "CalendarPage") {
      id = "NewCalendarPage";
      this.navSvc.goToInternalPage(id, { patChartId: 0 });
    } else if (id === "CalendarGridView") {
      this.navSvc.goToInternalPage(id, { backToDashboard: true });
    } else {
      this.navSvc.goToInternalPage(id, {});
    }
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/dashboard-navigation-bar/dashboard-navigation-bar.ts